import type { Flashcard,FlashcardReview } from './flashcards'

// The daily review queue shared by Flashcards, Revision and the sidebar line (api/src/flashcards.ts
// flashcardsToday applies the same rules): suspended cards never appear; directions already studied
// come back when due; never-studied directions are introduced at most newPerDay() per day, across all
// flashcards, counted from the viewer's local midnight.
export type Direction={id:string;front:string;back:string;frontArabic?:boolean;backArabic?:boolean}
export type QueueItem={card:Flashcard;direction:Direction}

export function directions(card:Flashcard):Direction[]{
 const out:Direction[]=[]
 if(card.card_type==='arabic'){
  if(card.arabic&&card.meaning)out.push({id:'arabic_meaning',front:card.arabic,back:card.meaning,frontArabic:true})
  if(card.reversible&&card.arabic&&card.meaning)out.push({id:'meaning_arabic',front:card.meaning,back:card.arabic,backArabic:true})
 }else{
  if(card.prompt&&card.answer)out.push({id:'forward',front:card.prompt,back:card.answer})
  if(card.reversible&&card.prompt&&card.answer)out.push({id:'reverse',front:card.answer,back:card.prompt})
 }
 return out
}

const NEW_PER_DAY_KEY='dawwin:flashcards:new-per-day',DEFAULT_NEW_PER_DAY=15
export function newPerDay(){try{const n=Number(localStorage.getItem(NEW_PER_DAY_KEY));return localStorage.getItem(NEW_PER_DAY_KEY)!==null&&Number.isFinite(n)?Math.max(0,Math.min(200,Math.round(n))):DEFAULT_NEW_PER_DAY}catch{return DEFAULT_NEW_PER_DAY}}
export function setNewPerDay(n:number){const v=Math.max(0,Math.min(200,Math.round(n)||0));try{localStorage.setItem(NEW_PER_DAY_KEY,String(v))}catch{}return v}
export function startOfToday(){const d=new Date();d.setHours(0,0,0,0);return d}

export const isSuspended=(card:Flashcard)=>Boolean(card.suspended_at)
export function reviewOf(card:Flashcard,d:Direction):FlashcardReview|undefined{return card.reviews?.find(r=>r.direction===d.id)}
export function isNew(card:Flashcard,d:Direction){return !reviewOf(card,d)}
export function isDue(card:Flashcard,d:Direction,now=Date.now()){const r=reviewOf(card,d);return !!r&&(!r.next_review_at||new Date(r.next_review_at).getTime()<=now)}
const dueTime=(item:QueueItem)=>{const r=reviewOf(item.card,item.direction);return r?.next_review_at?new Date(r.next_review_at).getTime():0}
// SQLite CURRENT_TIMESTAMP is UTC without a zone ("2026-10-27 18:02:11").
const sqliteTime=(s:string)=>new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s)?s:s.replace(' ','T')+'Z').getTime()

// Directions first studied today, across every non-suspended card (the cap is global, not per scope).
export function introducedToday(cards:Flashcard[],dayStart=startOfToday()){
 const since=dayStart.getTime()
 return cards.filter(c=>!isSuspended(c)).reduce((n,c)=>n+(c.reviews||[]).filter(r=>r.first_reviewed_at&&sqliteTime(r.first_reviewed_at)>=since).length,0)
}

export function allItems(cards:Flashcard[]):QueueItem[]{return cards.filter(c=>!isSuspended(c)).flatMap(card=>directions(card).map(direction=>({card,direction})))}

// Today's queue for a scope: due reviews (most overdue first), then new directions in the order the
// cards were created, limited by what is left of today's new-card allowance.
export function dailyQueue(scopeCards:Flashcard[],everyCard:Flashcard[],limit=newPerDay(),now=Date.now()){
 const items=allItems(scopeCards)
 const due=items.filter(i=>isDue(i.card,i.direction,now)).sort((a,b)=>dueTime(a)-dueTime(b))
 const unseen=items.filter(i=>isNew(i.card,i.direction)).sort((a,b)=>a.card.created_at.localeCompare(b.card.created_at)||a.card.id.localeCompare(b.card.id))
 const introduced=introducedToday(everyCard)
 const allowance=Math.max(0,limit-introduced)
 const fresh=unseen.slice(0,allowance)
 return {queue:[...due,...fresh],due,fresh,unseen:unseen.length,introduced,allowance,total:items.length}
}

// Rough time estimate for a session: about 10 seconds per review.
export const minutesFor=(n:number)=>n?Math.max(1,Math.round(n*10/60)):0
